import { NativeFormElements } from '../../global/ui-helpers.js';
import { canExpand } from './ui-field.helpers.js';

// List of virtual form elements that require special accessibility handling
const MultiInputVirtualFormElements = [
    'ui-monthpicker',
    'ui-monthrange',
    'ui-daterange',
    'ui-timepicker',
    'ui-timerange',
];

/**
 * Updates aria-labels for virtual form components to include the field label context.
 * This ensures screen readers announce the full context for each input within a virtual form control.
 * @param {HTMLElement} control - The form control element
 * @param {string} fieldLabel - The label text from the parent field
 */
export function updateAriaLabels(control, fieldLabel) {
    if (!control || !fieldLabel) {
        return;
    }

    const tagName = control.localName;
    if (!MultiInputVirtualFormElements.includes(tagName)) {
        return;
    }

    const fieldLabelText = fieldLabel.trim();
    const inputs = control.querySelectorAll(
        'input:not(.-hidden), select:not(.-hidden)'
    );

    inputs.forEach((input) => {
        const currentLabel = input.getAttribute('aria-label') || '';
        if (!currentLabel.includes(fieldLabelText)) {
            if (
                tagName === 'ui-timerange' ||
                (tagName === 'ui-timepicker' && control.closest('ui-timerange'))
            ) {
                // For timerange, append field label to existing time start/end labels
                const existingLabel = input.getAttribute('aria-label');
                input.setAttribute(
                    'aria-label',
                    `${existingLabel}, ${fieldLabelText}`
                );
            } else if (tagName === 'ui-timepicker') {
                // For standalone timepicker
                input.setAttribute('aria-label', `Time, ${fieldLabelText}`);
            } else {
                input.setAttribute(
                    'aria-label',
                    `${currentLabel}, ${fieldLabelText}`
                );
            }
        }
    });
}

/**
 * Handles click events for form control labels.
 * This includes:
 * 1. Expanding components that need to be expanded before focus
 * 2. Special handling for file upload dialogs
 * 3. Focusing the appropriate input element
 * @param {HTMLElement} control - The form control element
 * @returns {Promise<void>}
 */
export async function handleLabelClick(control) {
    if (!control) {
        return;
    }

    // For components that need to be expanded first (like dropdowns & datepickers)
    if (canExpand(control)) {
        control.expanded = true;
        // wait for the component to expand before assigning focus
        await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    // Special handling for upload - triggers native file dialog
    const tagName = control.localName;
    if (tagName === 'ui-upload') {
        const fileInput = control.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.click();
            return;
        }
    }

    // Find and focus the appropriate input element
    const elementToFocus = findFocusableElement(control);
    if (elementToFocus) {
        elementToFocus.focus();
    } else {
        control.focus();
    }
}

/**
 * Finds the appropriate focusable element within a form control.
 * Each component type may have different elements that should receive focus.
 * @param {HTMLElement} control - The form control element
 * @returns {HTMLElement|null} - The element that should receive focus
 * @private
 */
function findFocusableElement(control) {
    if (!control) {
        return null;
    }

    const tagName = control.localName;

    switch (tagName) {
        case 'ui-stepper':
            return control.querySelector('.ui-stepper__input');

        case 'ui-monthpicker':
            // Focus correct element based on control mode (different modes for desktop/mobile)
            return control.mode === 'input'
                ? control.querySelector('input[type="month"]:not(.-hidden)')
                : control.querySelector(
                      '.ui-monthpicker__control.-month:not(.-hidden)'
                  );

        case 'ui-monthrange':
            const firstMonthPicker = control.querySelector('ui-monthpicker');
            if (firstMonthPicker) {
                return firstMonthPicker.mode === 'input'
                    ? firstMonthPicker.querySelector(
                          'input[type="month"]:not(.-hidden)'
                      )
                    : firstMonthPicker.querySelector(
                          '.ui-monthpicker__control.-month:not(.-hidden)'
                      );
            }
            break;

        default:
            return control.querySelector(
                NativeFormElements.map(
                    (selector) =>
                        `${selector}:not(.-visually-hidden):not(.-hidden)`
                ).join(',')
            );
    }

    return null;
}
